import { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Send, Trash2, Plus, Loader2, Bot, User as UserIcon,
  Wand2, MessagesSquare, PlayCircle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { bolehPakaiAI } from "../lib/izinAI";

const SUGGESTIONS = [
  "Berapa omzet total bulan ini?",
  "Siapa Top 5 sales bulan ini?",
  "Siapa yang telat hari ini?",
  "Berapa cuti pending approval?",
  "Bikin ringkasan situasi bisnis hari ini",
  "Kasih 3 insight actionable dari data terkini",
];

export default function AIAssistant() {
  const { user, can, branding } = useAuth();
  // Nama toko dari pengaturan Branding (keputusan owner 24 Sep 2026, white-label)
  // — sama dengan persona yang dipakai backend (`ai_module._nama_toko`).
  const namaToko = branding?.nama_toko_sendiri || "EppleHub";
  // Gerbang fitur AI (brief owner 30 Agu 2026) — kunci yang SAMA dengan
  // gerbang backend di `backend/ai_akses.py`. Menu sidebar sudah memakai kunci
  // ini, tapi halaman tetap bisa dibuka lewat URL langsung, jadi gerbangnya
  // diulang di sini supaya tidak ada layar yang memuntahkan 403 beruntun.
  const bolehAI = bolehPakaiAI(user, can);
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [assistantDraft, setAssistantDraft] = useState("");
  const [triggering, setTriggering] = useState(false);
  const listRef = useRef(null);
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  const loadSessions = async () => {
    const { data } = await api.get("/ai/sessions");
    setSessions(data || []);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (bolehAI) loadSessions(); }, [bolehAI]);

  const loadMessages = async (id) => {
    if (!id) { setMessages([]); return; }
    try {
      const { data } = await api.get(`/ai/sessions/${id}/messages`);
      setMessages(data.messages || []);
    } catch { toast.error("Gagal muat pesan"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (bolehAI) loadMessages(activeId); }, [activeId, bolehAI]);

  useEffect(() => {
    // scroll bottom
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, assistantDraft]);

  const startNew = () => { setActiveId(null); setMessages([]); setInput(""); };

  const deleteSession = async (id) => {
    if (!confirm("Hapus percakapan ini?")) return;
    await api.delete(`/ai/sessions/${id}`);
    if (id === activeId) startNew();
    loadSessions();
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    setInput("");
    setStreaming(true);
    setAssistantDraft("");
    // Optimistically add user msg
    setMessages(m => [...m, { role: "user", content: msg, id: `tmp-${Date.now()}` }]);

    try {
      const token = localStorage.getItem("hris_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ session_id: activeId, message: msg }),
      });
      if (!res.ok) { toast.error("Gagal terhubung ke AI"); setStreaming(false); return; }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      let sessionIdFromServer = activeId;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const c of chunks) {
          if (!c.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(c.slice(5).trim());
            if (payload.chunk) { acc += payload.chunk; setAssistantDraft(acc); }
            else if (payload.done) {
              sessionIdFromServer = payload.session_id || activeId;
              setMessages(m => [...m, { role: "assistant", content: acc, id: `tmp-a-${Date.now()}` }]);
              setAssistantDraft("");
            } else if (payload.error) {
              toast.error("AI error: " + payload.error);
            }
          } catch { /* skip malformed line */ }
        }
      }
      if (!activeId && sessionIdFromServer) {
        setActiveId(sessionIdFromServer);
      }
      loadSessions();
    } catch (e) {
      toast.error(e.message || "Gagal");
    }
    setStreaming(false);
  };

  const triggerSummary = async () => {
    setTriggering(true);
    try {
      await api.post("/ai/trigger-summary");
      toast.success("Ringkasan AI harian di-generate & dikirim ke Telegram grup 🎉");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    setTriggering(false);
  };

  // Ditaruh SESUDAH seluruh hook dipanggil — early-return sebelum hook
  // melanggar Rules of Hooks dan membuat React error saat izin berubah.
  if (!bolehAI) {
    return (
      <div className="p-4 md:p-8 max-w-[1400px]" data-testid="ai-tanpa-izin">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center">
          <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="font-display text-lg font-bold text-slate-900">Fitur AI tidak aktif untuk akun ini</p>
          <p className="mt-2 text-sm text-slate-500">
            Akses AI diatur per akun oleh Owner/Admin lewat User Management
            (izin &ldquo;AI Assistant&rdquo;). Hubungi admin kalau kamu memang perlu memakainya.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> AI Assistant (GPT-5.2)
          </p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Asisten HRD {namaToko}</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">Tanya apa saja tentang omzet, absensi, cuti, leaderboard — jawaban di-generate live dari data database.</p>
        </div>
        {isAdmin && (
          <button onClick={triggerSummary} disabled={triggering} data-testid="btn-trigger-summary"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium shadow-sm">
            {triggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Trigger Ringkasan AI 20:00 sekarang
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-4">
        {/* Sidebar sessions */}
        <aside className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 space-y-2 h-fit">
          <button onClick={startNew} data-testid="btn-new-chat"
            className="w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Chat Baru
          </button>
          <div className="pt-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1 px-1"><MessagesSquare className="w-3 h-3 inline mr-1" /> Riwayat</p>
            {sessions.length === 0 ? (
              <p className="text-xs text-slate-400 p-2">Belum ada percakapan</p>
            ) : (
              <ul className="space-y-1 max-h-[500px] overflow-y-auto">
                {sessions.map(s => (
                  <li key={s.id}
                    className={`group flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer text-xs ${activeId === s.id ? "bg-emerald-50 border border-emerald-200" : "hover:bg-slate-50"}`}>
                    <button onClick={() => setActiveId(s.id)} className="flex-1 text-left truncate" data-testid={`ai-session-${s.id}`}>
                      {s.title || "(tanpa judul)"}
                    </button>
                    <button onClick={() => deleteSession(s.id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-rose-600">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Chat main */}
        <main className="bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col" style={{ height: "70vh" }}>
          <div ref={listRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
            {messages.length === 0 && !assistantDraft ? (
              <div className="h-full grid place-items-center text-center">
                <div>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-700 mx-auto mb-4 grid place-items-center text-white shadow-lg">
                    <Sparkles className="w-8 h-8" />
                  </div>
                  <p className="font-display text-lg font-bold text-slate-900">Halo {user?.email?.split("@")[0]}! 👋</p>
                  <p className="text-sm text-slate-500 mb-6">Coba salah satu prompt di bawah, atau tanyakan sendiri.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl">
                    {SUGGESTIONS.map((s, i) => (
                      <button key={i} onClick={() => send(s)} data-testid={`suggest-${i}`}
                        className="text-left text-sm px-4 py-3 rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors">
                        <Wand2 className="w-3.5 h-3.5 text-emerald-600 inline mr-1.5" />{s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {messages.map(m => (
                  <MessageBubble key={m.id} role={m.role} content={m.content} />
                ))}
                {assistantDraft && <MessageBubble role="assistant" content={assistantDraft} streaming />}
              </>
            )}
          </div>

          <div className="border-t border-slate-100 p-3 md:p-4">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
              <input value={input} onChange={e => setInput(e.target.value)}
                disabled={streaming}
                placeholder="Tanya apapun tentang bisnis Anda…" data-testid="ai-input"
                className="flex-1 h-11 px-4 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:bg-slate-50" />
              <button type="submit" disabled={streaming || !input.trim()} data-testid="ai-send"
                className="h-11 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {streaming ? "Menjawab..." : "Kirim"}
              </button>
            </form>
            <p className="text-[10px] text-slate-400 mt-2 text-center">Powered by GPT-5.2 · Data snapshot di-refresh otomatis di setiap turn</p>
          </div>
        </main>
      </div>
    </div>
  );
}

function MessageBubble({ role, content, streaming }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <div className={`w-8 h-8 rounded-full grid place-items-center flex-shrink-0 ${isUser ? "bg-slate-900 text-white" : "bg-gradient-to-br from-emerald-400 to-emerald-700 text-white shadow"}`}>
        {isUser ? <UserIcon className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>
      <div className={`max-w-[80%] rounded-xl px-4 py-3 ${isUser ? "bg-emerald-600 text-white" : "bg-slate-50 text-slate-900 border border-slate-100"}`}>
        <p className="text-sm whitespace-pre-wrap leading-relaxed">
          {content}
          {streaming && <span className="inline-block w-2 h-3.5 bg-emerald-500 animate-pulse ml-0.5 align-middle" />}
        </p>
      </div>
    </div>
  );
}
